import { Col, Row } from 'antd';
import clsx from 'clsx';
import Box from 'components/Box';
import Modal from 'components/Modal';
import Text from 'components/Text';
import { COMBO_PACKAGE_CASHIER_DATA, getComboIdFromSerUUID, savedComboDataWithBillDetail } from 'features/cashier/services/constants';
import { PAYMENT_TYPE } from 'features/payment/services/constants';
import { get, sumBy } from 'lodash';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { IResponseDataBody } from 'services/response';
import { uiLoadingBasicRef } from 'services/UI/LoadingPage/UILoadingRef';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import { momentTimezone } from 'utils/time';
import multiplePaymentSelectors from '../pages/PaymentPage/services/selectors';
import paymentApis from '../services/apis';
import { IBillDetailData } from '../services/types/bill';
import colorTheme from 'constants/color';
import { StarOutlined } from '@ant-design/icons';
export type ConfirmTipTicketRef = {
  open: (id: string, cb: () => void) => void;
  close: () => void;
};

export const useConfirmTipTicketRef = () => useRef<ConfirmTipTicketRef>(null);
type Props = {
  noReceipt?: () => void;
};
const ConfirmTipTicket = forwardRef<ConfirmTipTicketRef, Props>(({ noReceipt }, ref) => {
  const [open, setOpen] = useState(false);
  const cbRef = useRef<() => void>(() => undefined);
  const detailTicket = multiplePaymentSelectors.getDetail();
  const [detail, setDetail] = useState<IBillDetailData | null>(null);

  const calc = useMemo(() => ({
    serviceCharge: formatCurrency(detail?.cardFee),
    discByItem: formatCurrency(-(detail?.discountItem || 0)),
    discByTicket: formatCurrency(-(detail?.discountTicket || 0)),
    subTotal: formatCurrency((detail?.subTotal || 0) - ((detail?.discountItem || 0) + (detail?.discountTicket || 0))),
    balance: formatCurrency(detail?.subTotal),
    saleTax: formatCurrency(detail?.saleTax),
    useTax: formatCurrency(detail?.useTax),
    tip: formatCurrency(detail?.tip),
    total: formatCurrency(detail?.total),
  }), [detail]);

  const getDetailTicket = async (billId: string) => {
    uiLoadingBasicRef.current?.setVisible(true);
    try {
      const res: IResponseDataBody<IBillDetailData> = await paymentApis.getBillDetail(billId);
      if (res.data.data) return res.data.data;
      else return null;
    } catch (error) {
      return null;
    } finally {
      uiLoadingBasicRef.current?.setVisible(false);
    }
  };

  useImperativeHandle(ref, () => ({
    open: async (id: string, cb) => {
      if (!id) return;
      const data = await getDetailTicket(id);
      setDetail(data || detailTicket);
      setOpen(true);
      cbRef.current = cb;
    },
    close: () => setOpen(false),
  }));

  const handleClose = () => {
    setOpen(false);
    cbRef.current();
  };

  const RowTotal = () => {
    const cashPayment = detail?.payments.find(o => o.paymentType === PAYMENT_TYPE.CASH);
    const chance = cashPayment?.chance || 0;
    const cashAmount = cashPayment?.amount || 0;

    if (chance) return (
      <Row justify={'end'} align={'middle'} style={{ gap: '1.75rem' }}>
        <Row align={'bottom'}>
          <Text variant='H5' mb={0.25} mr={1} style={{ fontSize: '2rem' }}>Cash:</Text>
          <Text variant='H3' color='primary_active' style={{ fontSize: '2.5rem', color: '#D2464F', fontWeight: '700' }}>{formatCurrency(cashAmount + chance)}</Text>
        </Row>
        <Row align={'bottom'}>
          <Text variant='H5' mb={0.25} mr={1} style={{ fontSize: '2rem' }}>Change:</Text>
          <Text variant='H3' color='primary_active' style={{ fontSize: '2.5rem', color: '#D2464F', fontWeight: '700' }}>{formatCurrency(chance)}</Text>
        </Row>
        <Row align={'bottom'}>
          <Text variant='H5' mb={0.25} mr={1} style={{ fontSize: '2rem' }}>Total:</Text>
          <Text variant='H3' style={{ fontSize: '2.5rem', fontWeight: '700' }}>{formatCurrency(detail?.total)}</Text>
        </Row>
      </Row>
    );

    return (
      <Row justify={'end'} align={'bottom'}>
        <Text variant='H5' mb={0.25} mr={1} style={{ fontSize: '2rem' }}>Total:</Text>
        <Text variant='H3' color='primary_active' style={{ fontSize: '2.5rem', color: '#D2464F', fontWeight: '700' }}>{calc.total}</Text>
      </Row>
    );
  };

  if (!open) return null;

  return (
    <ModalStyled
      visible
      hiddenHeader
      hiddenFooter
      noneBodyStyle
      noneFooterStyle
      containerPadding={2}
      width={1000}
      className='modal-overflow-unset modal-max-height-unset'
      onClose={handleClose}
    >
      <TicketDetailStyled>
        <Row justify={'space-between'} style={{ marginBottom: '1rem' }}>
          <Row align={'middle'}>
            <Text variant='H5' mr={1} className='box-number'>
              <span>#{detail?.ticketNumber}</span>
            </Text>
            <Text variant='H5'>{detail?.startTime ? momentTimezone(detail?.startTime).format('hh:mm A') : null}</Text>
          </Row>
          <ServiceFeedbackCard>
            <div className="icon-star">
              <StarOutlined style={{ color: '#FFFFFF', fontSize: '28px' }} />
            </div>
            <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center'>
              <Text variant='H7' style={{ color: '#45979F' }}>
                Hi! How was your service today ?
              </Text>
              <Text>
                Please take a moment to rate your experience.
              </Text>
              <Text variant='CONTENT_1' color='v2_secondary_2' style={{ fontStyle: 'italic', fontWeight: '500', fontSize: '15px' }}>
                We truly appreciate your feedback!
              </Text>
              <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px', marginTop: '6px' }}>
                <div style={{ height: '1px', flex: 1, backgroundColor: '#6CB7BC' }} />
                <div style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: '#45979F' }} />
                <div style={{ height: '1px', flex: 1, backgroundColor: '#6CB7BC' }} />
              </div>
            </Box>
          </ServiceFeedbackCard>
          <Text variant='H5'>{detail?.customerName || 'Unknown Customer'}</Text>
        </Row>
        <div className="items">
          {detail?.items.map(o => (
            <div key={o.staffId} className='item'>
              <Row align={'middle'} style={{ width: '100%', fontSize: '1.5rem' }} justify={'space-between'}><Text variant='H9'>{o.staffName}</Text><Text variant='H8' color='info_3' style={{ fontWeight: '700', fontSize: '1.5rem' }} >TIP: {formatCurrency(o.tip)}</Text></Row>
              <Text className='border-dotted' mb={0.5} />
              {o.services.map((s, i) => {
                const comboId = getComboIdFromSerUUID(s?.clientId || '');
                const comboData = get(COMBO_PACKAGE_CASHIER_DATA, comboId);
                const isIndex0 = comboData?.services?.findIndex(ser => ser.uuid_local === s.clientId) === 0;

                return (
                  <>
                    {isIndex0 && <Row key={comboId} align={'middle'} justify={'space-between'} className='service comboData'>
                      <Box display='flex' alignItems='center' gap='2'>
                        <Text variant='CONTENT_1'>{comboData?.data?.serviceName || ''}</Text>
                        <p className="package-label">package</p>
                      </Box>
                      <Text variant='CONTENT_2'>{formatCurrency(sumBy(comboData?.services, ser => ser.price))}</Text>
                    </Row>}
                    <Row key={i} align={'middle'} justify={'space-between'} className={clsx('service', comboData && 'isCombo')}>
                      <Text variant='CONTENT_1'>{s.itemName}</Text>
                      <Text variant='CONTENT_2'>{formatCurrency(s.price || 0)}</Text>
                    </Row>
                  </>
                );
              })}
            </div>
          ))}
        </div>
        <Text className="border" mb={2} />
        <Row className="group-info">
          <Col flex={'auto'}>
            <div className="item">
              <Text variant='H8'>Processing Fee:</Text>
              <Text variant='H7'>{calc.serviceCharge}</Text>
            </div>
            <div className="item">
              <Text variant='H8'>Disc. By Item:</Text>
              <Text variant='H7'>{calc.discByItem}</Text>
            </div>
            <div className="item">
              <Text variant='H8'>Disc. By Ticket:</Text>
              <Text variant='H7'>{calc.discByTicket}</Text>
            </div>
            <div className="item">
              <Text variant='H8'>Subtotal:</Text>
              <Text variant='H7'>{calc.subTotal}</Text>
            </div>

          </Col>
          <Col flex={'auto'} />
          <Col flex={'auto'}>
            <div className="item">
              <Text variant='H8'>Balance:</Text>
              <Text variant='H7'>{calc.balance}</Text>
            </div>
            <div className="item">
              <Text variant='H8'>Sale Tax:</Text>
              <Text variant='H7'>{calc.saleTax}</Text>
            </div>
            <div className="item">
              <Text variant='H8'>Use Tax:</Text>
              <Text variant='H7'>{calc.useTax}</Text>
            </div>
            <div className="item">
              <Text variant='H8' color='primary_active' style={{ fontWeight: '700', color: '#D2464F' }}>Tip:</Text>
              <Text variant='H7' color='primary_active' style={{ fontWeight: '700', color: '#D2464F' }}>{calc.tip}</Text>
            </div>
          </Col>
        </Row>
        <Text className="border" mt={1} mb={1} />
        <RowTotal />
        <Text my={1} />
      </TicketDetailStyled>
      <Row justify={'center'} style={{ gap: 16 }}>
        {noReceipt && <button className='buttonCompleted no-receipt' onClick={noReceipt}>
          <div className='icon-tick'><IconTick /></div>
          <Text variant='H7' color='text_3' style={{ color: '#292C33', padding: '0 1rem' }}>No Receipt</Text>
        </button>}
        <button className='buttonCompleted' onClick={handleClose}>
          <div className='icon-tick'><IconTick /></div>
          <Text variant='H7' color='text_3' style={{ color: '#fff', padding: '0 1rem' }}>Completed</Text>
        </button>
      </Row>
    </ModalStyled>
  );
});
ConfirmTipTicket.displayName = 'ConfirmTipTicket';
export default ConfirmTipTicket;
const ModalStyled = styled(Modal)`
.buttonCompleted {
  display: flex;
  align-items: center;
  border-radius: 6px;
  height: 56px;
  background: #292C33;
  border-radius: 8px;
  overflow: hidden;
  .icon-tick {
    width: 56px;
    background: #759F50;
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &.no-receipt {
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    border: 1.5px solid #292C33;

  }
}
`;
const TicketDetailStyled = styled.div`
.btn-back {
  position: fixed;
  top: 0.5rem;
  right: 2.5rem;
}
.box-number {
  background: #000;
  padding: 4px 8px;
  border-radius: 4px;
  span {
    color: #fff;
  }
}
  .items {
    max-height: 50vh;
    overflow: auto;
    margin: 0 -2rem;
    padding: 0 2rem;
    margin-right: -1.5rem;
    padding-right: 1rem;
    .border-dotted {
      border: 1px dotted #86909C;
    }
    .item {
      margin-bottom: 1rem;
    }
    .service {
      margin-bottom: 0.5rem;

      &.comboData {
        .CONTENT_1 {
        font-size: 18px !important;
        }
        .CONTENT_2 {
          font-size: 24px !important;
        }

        .package-label {
          color: #1D2129;
          font-family: Poppins;
          font-size: 14px;
          font-style: normal;
          font-weight: bolder;
          line-height: normal;
        }
      }
      &.isCombo {
        margin-left: 2rem;
      }
    }
  }
  .border {
    border: 0.75px solid #86909C;
  }
  .box-info {
    margin-bottom: 2rem;
  }
  .group-info {
    .item {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0.5rem;
      .H8, .H7 {
        font-size: 1.5rem;
      }
    }
  }
`;

const IconTick = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M9.72692 18C9.40676 18 9.10261 17.8661 8.8785 17.6317L4.34817 12.8936C3.88394 12.4081 3.88394 11.6045 4.34817 11.1189C4.8124 10.6334 5.58078 10.6334 6.04501 11.1189L9.72692 14.9697L17.955 6.36414C18.4192 5.87862 19.1876 5.87862 19.6518 6.36414C20.1161 6.84967 20.1161 7.6533 19.6518 8.13882L10.5753 17.6317C10.3512 17.8661 10.0471 18 9.72692 18Z" fill="#fff" />
  </svg>
);


const ServiceFeedbackCard = styled.div`
  display: flex;
  align-items: start;
  justify-content: center;
  gap: 1rem;
  font-family: 'Poppins';
  background: ${colorTheme.v2_secondary_6};
  border: 1px solid ${colorTheme.v2_secondary_4};
  border-radius: 8px;
  padding: 8px 16px;

  .icon-star {
    width: 48px;
    height: 48px;
    background: #45979F;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;